import { Button, Stack } from '@mui/material';
import type { Swm_UpsertStaffAbsenceType } from '@tyro/api';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  RHFTextField,
  useFormValidator,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  type ReturnTypeFromUseAbsenceTypes,
  useCreateOrUpdateAbsenceType,
} from '../../api/staff-work-absence-types';

export type EditAbsenceTypeFormState = Pick<
  Swm_UpsertStaffAbsenceType,
  'absenceTypeId' | 'code'
> & {
  name: string;
  description?: string | null;
};

export type EditAbsenceTypeModalProps = {
  initialAbsenceTypeState?: Partial<EditAbsenceTypeFormState> | undefined;
  absenceTypes: ReturnTypeFromUseAbsenceTypes[];
  onClose: () => void;
};

export const EditAbsenceTypeModal = ({
  initialAbsenceTypeState,
  absenceTypes,
  onClose,
}: EditAbsenceTypeModalProps) => {
  const { t } = useTranslation(['common', 'absence']);
  const {
    mutate: createOrUpdateAbsenceTypeMutation,
    isPending: isSubmitting,
    isSuccess: isSubmitSuccessful,
  } = useCreateOrUpdateAbsenceType();

  const { resolver, rules } = useFormValidator<EditAbsenceTypeFormState>();

  const absenceTypesWithoutSelf = absenceTypes.filter(
    (absenceType) => absenceType?.code !== initialAbsenceTypeState?.code,
  );

  const { control, handleSubmit, reset } = useForm<EditAbsenceTypeFormState>({
    resolver: resolver({
      name: [
        rules.required(),
        rules.isUniqueByKey(
          absenceTypesWithoutSelf,
          'name',
          t('absence:absenceTypeNameShouldBeUnique'),
        ),
      ],
      code: [rules.required()],
      description: [rules.required()],
    }),
    defaultValues: initialAbsenceTypeState,
  });

  const onSubmit = ({
    name,
    description,
    ...restData
  }: EditAbsenceTypeFormState) => {
    createOrUpdateAbsenceTypeMutation(
      [
        {
          name: [{ locale: 'en', value: name }],
          description: [{ locale: 'en', value: description }],
          availableForRequests: true,
          ...restData,
        },
      ],
      {
        onSuccess: onClose,
      },
    );
  };

  useEffect(() => {
    if (initialAbsenceTypeState) {
      reset(initialAbsenceTypeState);
    }
  }, [initialAbsenceTypeState]);

  useEffect(() => {
    reset();
  }, [isSubmitSuccessful]);

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Dialog
      open={!!initialAbsenceTypeState}
      onClose={handleClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle onClose={onClose}>
        {initialAbsenceTypeState?.absenceTypeId
          ? t('absence:editAbsenceType')
          : t('absence:createAbsenceType')}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3} p={3}>
          <Stack direction="row" gap={2}>
            <RHFTextField
              label={t('absence:absenceTypeName')}
              controlProps={{
                name: 'name',
                control,
              }}
              textFieldProps={{
                fullWidth: true,
              }}
            />
            <RHFTextField
              label={t('absence:absenceCode')}
              controlProps={{
                name: 'code',
                control,
              }}
              textFieldProps={{
                fullWidth: true,
              }}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <RHFTextField
              label={t('common:description')}
              controlProps={{
                name: 'description',
                control,
              }}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                minRows: 3,
              }}
            />
          </Stack>
        </Stack>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>

          <Button type="submit" variant="contained" loading={isSubmitting}>
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
